import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, IconButton, Dialog, DialogContent, DialogActions, TextField,
  MenuItem, Chip, Autocomplete, Avatar, Stack, ToggleButton, ToggleButtonGroup, InputAdornment,
  CircularProgress, useMediaQuery, Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import AddIcon from '@mui/icons-material/Add';
import apiService from '../../services/apiServices';
import {
  DEPARTMENTS, STATUS_OPTIONS, PRIORITY_OPTIONS, PROJECT_COLORS, workerName,
} from './projectConstants';

/**
 * Formulari i projektit — një i vetëm, për krijim dhe për redaktim.
 *
 * Deri tani dy mënyrat ndanin të njëjtin dialog, por dalloheshin pikërisht aty
 * ku s'duhej: redaktimi fuste një shënim nën departament që e shtynte rreshtin
 * poshtë, butoni e ndryshonte emrin dhe nëntitulli ndryshonte formë. Më keq,
 * redaktimi dërgonte vetëm fushat që shfaqeshin, dhe serveri — që e rishkruan
 * tërë rreshtin — ia kthente ngjyrën projektit në të parazgjedhurën, buxhetin e
 * orëve në zero dhe ekipin bosh. Tani forma është identike në të dy rastet, dhe
 * redaktimi ruan çdo fushë që s'e prek.
 */

const EMPTY = {
  name: '', department: '', client_id: '', color: PROJECT_COLORS[0],
  manager_id: '', members: [],
  status: 'draft', priority: 'medium', start_date: '', end_date: '', budget_hours: '',
  description: '',
};

const dateInput = (v) => (v ? String(v).slice(0, 10) : '');
const wid = (w) => String(w?.Id ?? w?.id ?? '');
const initials = (w) => workerName(w).split(' ').filter(Boolean).slice(0, 2).map((s) => s[0]).join('').toUpperCase() || '?';

function fromProject(p) {
  if (!p) return { ...EMPTY };
  const members = Array.isArray(p.members) ? p.members : (Array.isArray(p.Members) ? p.Members : []);
  return {
    name:         p.name || p.Name || '',
    department:   String(p.department || p.Department || '').toLowerCase(),
    client_id:    p.client_id || p.ClientId || '',
    color:        p.color || p.Color || PROJECT_COLORS[0],
    manager_id:   p.manager_id || p.ManagerId || '',
    members:      members.map(Number).filter(Boolean),
    status:       String(p.status || p.Status || 'draft').toLowerCase(),
    priority:     String(p.priority || p.Priority || 'medium').toLowerCase(),
    start_date:   dateInput(p.start_date || p.StartDate),
    end_date:     dateInput(p.end_date || p.EndDate),
    budget_hours: Number(p.budget_hours ?? p.BudgetHours) ? String(p.budget_hours ?? p.BudgetHours) : '',
    description:  p.description || p.Description || '',
  };
}

function daysBetween(a, b) {
  if (!a || !b) return null;
  const d = Math.round((new Date(b) - new Date(a)) / 86400000) + 1;
  return d > 0 ? d : null;
}

function Section({ step, title, hint, children }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '200px 1fr' }, gap: { xs: 1.5, md: 3 }, py: 2.5,
      '&:not(:last-of-type)': { borderBottom: '1px solid', borderColor: 'divider' } }}>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: 'action.selected', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '.7rem', fontWeight: 800, color: 'text.secondary' }}>{step}</Box>
          <Typography sx={{ fontWeight: 800, fontSize: '.9rem' }}>{title}</Typography>
        </Stack>
        {hint && <Typography sx={{ fontSize: '.74rem', color: 'text.secondary', mt: 0.5, pl: { md: 3.75 } }}>{hint}</Typography>}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignContent: 'start' }}>
        {children}
      </Box>
    </Box>
  );
}

const Dot = ({ color, size = 8 }) => <Box component="span" sx={{ width: size, height: size, borderRadius: '50%', bgcolor: color, flexShrink: 0, display: 'inline-block' }} />;

export default function ProjectFormDialog({ open, project, workers = [], clients = [], onClose, onSaved }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const editing = Boolean(project);
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');
  // Klientët mbahen lokalisht që një i sapokrijuar të dalë menjëherë te lista,
  // pa pritur ringarkimin e faqes prapa dialogut.
  const [clientList, setClientList] = useState(clients);
  const [clientOpen, setClientOpen] = useState(false);
  const [newClient, setNewClient] = useState('');
  const [clientError, setClientError] = useState('');
  const [clientSaving, setClientSaving] = useState(false);

  useEffect(() => { setClientList(clients); }, [clients]);

  const createClient = async () => {
    const name = newClient.trim();
    if (!name) { setClientError('Emri i klientit është i detyrueshëm.'); return; }
    if (clientList.some((c) => String(c.name).toLowerCase() === name.toLowerCase())) {
      setClientError('Ky klient ekziston tashmë.');
      return;
    }
    setClientSaving(true); setClientError('');
    try {
      const res = await apiService().post('clients', { name, status: 'Active' });
      const created = res?.data || res;
      const id = created?.id ?? created?.Id;
      if (!id) throw new Error('Krijimi dështoi');
      setClientList((l) => [...l, { id, name }]);
      setF((x) => ({ ...x, client_id: id }));
      setClientOpen(false);
    } catch (e) {
      setClientError(e?.message || 'Krijimi dështoi. Provo sërish.');
    } finally { setClientSaving(false); }
  };

  useEffect(() => {
    if (open) { setF(fromProject(project)); setErrors({}); setServerError(''); }
  }, [open, project]);

  const set = (k) => (e) => { const v = e?.target ? e.target.value : e; setF((p) => ({ ...p, [k]: v })); setErrors((x) => ({ ...x, [k]: undefined })); };

  const dept = DEPARTMENTS.find((d) => d.value === f.department);
  const code = editing ? (project.code || project.Code || '') : '';
  const codePreview = code || (dept ? `${dept.prefix}-${new Date().getFullYear()}-···` : 'Zgjidh departamentin');
  const duration = daysBetween(f.start_date, f.end_date);
  const workerById = useMemo(() => { const m = new Map(); workers.forEach((w) => m.set(wid(w), w)); return m; }, [workers]);
  const manager = workerById.get(String(f.manager_id)) || null;
  const members = f.members.map((id) => workerById.get(String(id))).filter(Boolean);

  const validate = () => {
    const e = {};
    if (!f.name.trim()) e.name = 'Emri i projektit është i detyrueshëm';
    if (!f.department) e.department = 'Zgjidh departamentin';
    if (f.start_date && f.end_date && f.end_date < f.start_date) e.end_date = 'Duhet të jetë pas datës së fillimit';
    if (f.budget_hours !== '' && (Number.isNaN(Number(f.budget_hours)) || Number(f.budget_hours) < 0)) e.budget_hours = 'Numër pozitiv';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true); setServerError('');
    // Fushat që forma nuk i shfaq (p.sh. `team`) kthehen siç ishin: serveri e
    // rishkruan tërë rreshtin, dhe një fushë që mungon do të fshihej.
    const keep = editing ? { team: project.team ?? project.Team ?? [] } : {};
    const payload = {
      ...keep,
      name:         f.name.trim(),
      department:   f.department || null,
      client_id:    f.client_id || null,
      color:        f.color,
      manager_id:   f.manager_id || null,
      members:      f.members,
      status:       f.status,
      priority:     f.priority,
      start_date:   f.start_date || null,
      end_date:     f.end_date || null,
      budget_hours: f.budget_hours === '' ? 0 : Number(f.budget_hours),
      description:  f.description.trim(),
    };
    try {
      const res = editing
        ? await apiService().put('projects', { ...payload, id: project.id ?? project.Id })
        : await apiService().post('projects', payload);
      if (res && res.success === false) throw new Error(res.error || 'Ruajtja dështoi');
      onSaved?.(res?.data, editing);
    } catch (e) {
      setServerError(e?.message || 'Ruajtja dështoi. Provo sërish.');
    } finally { setSaving(false); }
  };

  const close = () => { if (!saving) onClose?.(); };
  const accent = f.color || theme.palette.primary.main;

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3, overflow: 'hidden' } }}>
      {/* Koka — e njëjta formë në krijim dhe redaktim; ndryshon vetëm titulli. */}
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
        borderBottom: '1px solid', borderColor: 'divider', bgcolor: alpha(accent, theme.palette.mode === 'dark' ? 0.14 : 0.05) }}>
        <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          boxShadow: `0 4px 12px ${alpha(accent, 0.35)}`, transition: 'background-color .2s' }}>
          <FolderRoundedIcon sx={{ color: '#fff', fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 800, fontSize: '1.1rem', lineHeight: 1.25 }}>
            {editing ? (f.name.trim() || 'Edito projektin') : (f.name.trim() || 'Projekt i ri')}
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.3 }}>
            <Typography sx={{ fontSize: '.72rem', color: 'text.secondary', fontWeight: 600 }}>
              {editing ? 'Redaktim' : 'Krijim'} ·
            </Typography>
            <Chip size="small" label={codePreview}
              sx={{ height: 20, fontFamily: 'monospace', fontWeight: 700, fontSize: '.68rem', bgcolor: 'action.selected' }} />
          </Stack>
        </Box>
        <IconButton size="small" onClick={close} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 0 }}>
        <Section step={1} title="Informacioni bazë" hint="Departamenti përcakton prefiksin e kodit; kodi mbetet i njëjtë pas krijimit.">
          <TextField label="Emri i projektit" required fullWidth autoFocus={!editing} placeholder="p.sh. Implementimi i ERP"
            value={f.name} onChange={set('name')} error={Boolean(errors.name)} helperText={errors.name}
            sx={{ gridColumn: '1 / -1' }} />
          <TextField select label="Departamenti" required fullWidth value={f.department} onChange={set('department')}
            error={Boolean(errors.department)} helperText={errors.department}
            SelectProps={{ renderValue: (v) => DEPARTMENTS.find((d) => d.value === v)?.label || v }}>
            {DEPARTMENTS.map((d) => (
              <MenuItem key={d.value} value={d.value}>
                <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                  <span>{d.label}</span>
                  <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '.72rem', color: 'text.secondary' }}>{d.prefix}</Typography>
                </Stack>
              </MenuItem>
            ))}
          </TextField>
          {/* Klienti i ri shtohet pa e humbur formularin: kur emri i kërkuar
              nuk ekziston ende, dalja te faqja e klientëve do të thoshte humbje
              e gjithçkaje të shkruar deri këtu. Krijohet vetëm emri dhe statusi;
              kontaktet plotësohen më vonë te «Klientët». */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField select label="Klienti / Kompania" fullWidth value={f.client_id} onChange={set('client_id')}>
              <MenuItem value=""><em>Pa klient (projekt i brendshëm)</em></MenuItem>
              {clientList.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <Tooltip title="Shto një klient të ri">
              <span>
                <IconButton size="small" onClick={() => { setNewClient(''); setClientError(''); setClientOpen(true); }}
                  sx={{ mt: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography sx={{ fontSize: '.74rem', color: 'text.secondary', fontWeight: 600, mb: 0.75 }}>Ngjyra</Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              {PROJECT_COLORS.map((c) => (
                <Tooltip key={c} title={c}>
                  <Box component="button" type="button" onClick={() => set('color')(c)} aria-label={`Ngjyra ${c}`}
                    sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: c, cursor: 'pointer', p: 0,
                      border: '2px solid', borderColor: f.color === c ? 'text.primary' : 'transparent',
                      outline: `2px solid ${theme.palette.background.paper}`, outlineOffset: -4,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {f.color === c && <CheckRoundedIcon sx={{ fontSize: 15, color: '#fff' }} />}
                  </Box>
                </Tooltip>
              ))}
            </Stack>
          </Box>
        </Section>

        <Section step={2} title="Ekipi" hint="Menaxheri mban përgjegjësinë; anëtarët shohin projektin dhe regjistrojnë orë në të.">
          <Autocomplete
            options={workers} value={manager}
            getOptionLabel={(o) => workerName(o)}
            isOptionEqualToValue={(o, v) => wid(o) === wid(v)}
            onChange={(_, v) => set('manager_id')(v ? (v.Id || v.id) : '')}
            renderOption={(props, o) => (
              <li {...props} key={wid(o)}>
                <Avatar sx={{ width: 26, height: 26, fontSize: 11, mr: 1.25 }}>{initials(o)}</Avatar>
                <Box>
                  <Typography sx={{ fontSize: '.85rem', fontWeight: 600 }}>{workerName(o)}</Typography>
                  {(o.Position || o.position) && <Typography sx={{ fontSize: '.7rem', color: 'text.secondary' }}>{o.Position || o.position}</Typography>}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Menaxheri i projektit" placeholder="Zgjidh menaxherin"
                InputProps={{ ...params.InputProps, startAdornment: manager ? (
                  <InputAdornment position="start"><Avatar sx={{ width: 24, height: 24, fontSize: 10 }}>{initials(manager)}</Avatar></InputAdornment>
                ) : params.InputProps.startAdornment }} />
            )}
            sx={{ gridColumn: '1 / -1' }}
          />
          <Autocomplete
            multiple disableCloseOnSelect
            options={workers} value={members}
            getOptionLabel={(o) => workerName(o)}
            isOptionEqualToValue={(o, v) => wid(o) === wid(v)}
            onChange={(_, val) => set('members')(val.map((v) => Number(v.Id || v.id)))}
            renderOption={(props, o, { selected }) => (
              <li {...props} key={wid(o)}>
                <Avatar sx={{ width: 26, height: 26, fontSize: 11, mr: 1.25, bgcolor: selected ? accent : undefined }}>
                  {selected ? <CheckRoundedIcon sx={{ fontSize: 15 }} /> : initials(o)}
                </Avatar>
                <Box>
                  <Typography sx={{ fontSize: '.85rem', fontWeight: 600 }}>{workerName(o)}</Typography>
                  {(o.Position || o.position) && <Typography sx={{ fontSize: '.7rem', color: 'text.secondary' }}>{o.Position || o.position}</Typography>}
                </Box>
              </li>
            )}
            renderTags={(value, getTagProps) => value.map((o, i) => (
              <Chip size="small" avatar={<Avatar>{initials(o)}</Avatar>} label={workerName(o)} {...getTagProps({ index: i })} key={wid(o)} />
            ))}
            renderInput={(params) => (
              <TextField {...params} label="Anëtarët e projektit" placeholder={members.length ? '' : 'Zgjidh punonjës'}
                helperText={`${members.length} ${members.length === 1 ? 'anëtar' : 'anëtarë'}`} />
            )}
            sx={{ gridColumn: '1 / -1' }}
          />
        </Section>

        <Section step={3} title="Planifikimi" hint="Statusi, rëndësia dhe afatet e projektit.">
          <TextField select label="Statusi" fullWidth value={f.status} onChange={set('status')}
            SelectProps={{ renderValue: (v) => { const s = STATUS_OPTIONS.find((x) => x.value === v); return (
              <Stack direction="row" spacing={1} alignItems="center"><Dot color={s?.dot || '#94A3B8'} /><span>{s?.label || v}</span></Stack>
            ); } }}>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s.value} value={s.value}><Stack direction="row" spacing={1} alignItems="center"><Dot color={s.dot} /><span>{s.label}</span></Stack></MenuItem>
            ))}
          </TextField>
          <TextField label="Buxheti i orëve" fullWidth type="number" value={f.budget_hours} onChange={set('budget_hours')}
            inputProps={{ min: 0, step: 1 }} placeholder="Pa kufi" error={Boolean(errors.budget_hours)} helperText={errors.budget_hours}
            InputProps={{ endAdornment: <InputAdornment position="end">orë</InputAdornment> }} />
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography sx={{ fontSize: '.74rem', color: 'text.secondary', fontWeight: 600, mb: 0.75 }}>Prioriteti</Typography>
            <ToggleButtonGroup exclusive fullWidth size="small" value={f.priority} onChange={(_, v) => v && set('priority')(v)}>
              {PRIORITY_OPTIONS.map((p) => (
                <ToggleButton key={p.value} value={p.value}
                  sx={{ textTransform: 'none', fontWeight: 700, gap: 0.75, py: 0.9,
                    '&.Mui-selected': { bgcolor: alpha(p.color, 0.12), color: p.color, borderColor: alpha(p.color, 0.5) },
                    '&.Mui-selected:hover': { bgcolor: alpha(p.color, 0.18) } }}>
                  <Dot color={p.color} /> {p.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <TextField label="Data e fillimit" type="date" fullWidth InputLabelProps={{ shrink: true }}
            value={f.start_date} onChange={set('start_date')} />
          <TextField label="Data e përfundimit" type="date" fullWidth InputLabelProps={{ shrink: true }}
            value={f.end_date} onChange={set('end_date')} error={Boolean(errors.end_date)}
            helperText={errors.end_date || (duration ? `Kohëzgjatja: ${duration} ditë` : ' ')}
            inputProps={{ min: f.start_date || undefined }} />
        </Section>

        <Section step={4} title="Përshkrimi" hint="Qëllimi, fushëveprimi dhe çfarë konsiderohet «e kryer».">
          <TextField fullWidth multiline minRows={4} maxRows={10} placeholder="Përshkruaj projektin…"
            value={f.description} onChange={set('description')} inputProps={{ maxLength: 2000 }}
            helperText={`${f.description.length}/2000`} FormHelperTextProps={{ sx: { textAlign: 'right' } }}
            sx={{ gridColumn: '1 / -1' }} />
        </Section>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.75, gap: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
        <Typography sx={{ flex: 1, fontSize: '.74rem', color: serverError ? 'error.main' : 'text.secondary' }}>
          {serverError || '* Fushat e detyrueshme'}
        </Typography>
        <Button onClick={close} disabled={saving}
          sx={{ textTransform: 'none', borderRadius: 2, px: 2.5, border: '1px solid', borderColor: 'divider', color: 'text.primary' }}>
          Anulo
        </Button>
        <Button onClick={save} variant="contained" disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <CheckRoundedIcon />}
          sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, px: 2.5 }}>
          {editing ? 'Ruaj ndryshimet' : 'Krijo projektin'}
        </Button>
      </DialogActions>

      {/* Klient i ri — vetëm emri. Kontaktet, menaxheri dhe lidhjet me projekte
          plotësohen te «Klientët»; këtu duhet aq sa ta zgjedhësh dhe të vazhdosh. */}
      <Dialog open={clientOpen} onClose={() => !clientSaving && setClientOpen(false)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Klient i ri</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Shtohet te lista dhe zgjidhet menjëherë te ky projekt. Detajet e tjera i plotëson më vonë te «Klientët».
          </Typography>
          <TextField
            autoFocus fullWidth required label="Emri i klientit"
            value={newClient}
            onChange={(e) => { setNewClient(e.target.value); if (clientError) setClientError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && !clientSaving) createClient(); }}
            error={Boolean(clientError)}
            helperText={clientError || ' '}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setClientOpen(false)} disabled={clientSaving}
            sx={{ textTransform: 'none', borderRadius: 2 }}>Anulo</Button>
          <Button onClick={createClient} variant="contained" disabled={clientSaving || !newClient.trim()}
            startIcon={clientSaving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700 }}>Shto</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
